import { Button, Modal, Form, Alert, InputGroup } from "react-bootstrap";
import { MakeModel, makeAPI } from "../../api/makeAPI";
import { Problem, ValidationProblem, formErrors } from "../../api/backendApi";
import { SyntheticEvent, useState } from "react";
import ErrorDisplay from "../../common/ErrorDisplay";
import LoadingDisplay from "../../common/LoadingDisplay";

interface MakeModalProps {
  make: MakeModel;
  isCreate: boolean;
  onChange: (make: MakeModel) => void;
  onCancel: () => void;
  modalText: string;
}

function MakeModal({
  make: initialMake,
  isCreate,
  onChange,
  onCancel,
  modalText,
}: MakeModalProps) {
  const [make, setMake] = useState(initialMake);
  const [errors, setErrors] = useState<formErrors>({});
  const [enteredMakeCode, setEnteredMakeCode] = useState<string>("");
  const [availableMakeCodes, setAvailableMakeCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Problem | undefined>(undefined);

  const [show, setShow] = useState(true);

  const handleClose = () => {
    setShow(false);
    onCancel();
    setMake(initialMake);
  };

  const getAvailableMakeCodes = async () => {
    setLoading(true);
    setError(undefined);

    let response = await makeAPI.getAvailableMakeCodes(enteredMakeCode);

    if (response.ok) {
      setAvailableMakeCodes(response.value);
    } else {
      setError(response.problem);
    }
    setLoading(false);
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;

    const change = { [name]: value };
    const modified: MakeModel = { ...make, ...change };

    if (name === "code") {
      setEnteredMakeCode(value);
    }

    setMake(modified);
  };

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();

    let call = isCreate ? makeAPI.createMake(make) : makeAPI.updateMake(make);

    call.then((result) => {
      if (result.ok) {
        onChange(make);
        handleClose();
      } else {
        let validation = result.problem as ValidationProblem;
        if (validation.validation) {
          setErrors(validation.validation);
        } else {
          let errors: formErrors = {};
          errors[""] = [`${result.problem.title} [${result.problem.instance}]`];
          setErrors(errors);
        }
      }
    });
  };

  return (
    <>
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{modalText}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ErrorDisplay error={error} />
          {availableMakeCodes?.length === 1 && (
            <Alert variant="success">This make code is available</Alert>
          )}
          {availableMakeCodes?.length > 1 && (
            <>
              <Alert variant="info">
                Found {availableMakeCodes.length} available make codes, please
                select any of the available options
              </Alert>
            </>
          )}
          <Form onSubmit={handleSubmit} id="makeForm">
            {errors[""] && (
              <div style={{ marginTop: "1rem" }}>
                {errors[""].map((errorMessage, index) => (
                  <Alert key={errorMessage} variant="danger">
                    <p key={index}>{errorMessage}</p>
                  </Alert>
                ))}
              </div>
            )}
            <Form.Group className="mb-3">
              {!isCreate && (
                <div>
                  <Form.Label>Code</Form.Label>
                  <Form.Control
                    name="code"
                    type="text"
                    id="code"
                    defaultValue={make.code}
                    disabled
                    readOnly
                  />
                </div>
              )}
              {isCreate && availableMakeCodes.length === 0 && (
                <div>
                  <Form.Label>Code</Form.Label>
                  <InputGroup className="mb-3">
                    <Form.Control
                      name="code"
                      type="text"
                      id="code"
                      defaultValue={undefined}
                      placeholder="Code..."
                      onChange={handleChange}
                      maxLength={2}
                    />
                    <Button
                      type="button"
                      disabled={loading}
                      onClick={getAvailableMakeCodes}
                    >
                      <LoadingDisplay loading={loading} />
                      Check availability
                    </Button>
                  </InputGroup>
                </div>
              )}
              {isCreate && availableMakeCodes.length === 1 && (
                <div>
                  <Form.Label>Code</Form.Label>
                  <InputGroup className="mb-3">
                    <Form.Control
                      name="code"
                      type="text"
                      id="code"
                      defaultValue={enteredMakeCode}
                      disabled
                      onChange={handleChange}
                      maxLength={2}
                    />
                    <Button
                      type="button"
                      onClick={() => {
                        setAvailableMakeCodes([]);
                        setEnteredMakeCode("");
                      }}
                    >
                      Cancel
                    </Button>
                  </InputGroup>
                </div>
              )}
              {isCreate && availableMakeCodes.length > 1 && (
                <div>
                  <Form.Label>Code</Form.Label>
                  <InputGroup className="mb-3">
                    <Form.Select
                      name="code"
                      id="toolbarMakeCodes"
                      onChange={handleChange}
                    >
                      <option key="" value="">
                        [Select Make Code]
                      </option>
                      {availableMakeCodes?.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </Form.Select>
                    <Button
                      type="button"
                      onClick={() => {
                        setAvailableMakeCodes([]);
                        setEnteredMakeCode("");
                      }}
                    >
                      Cancel
                    </Button>
                  </InputGroup>
                </div>
              )}
              {availableMakeCodes?.length > 0 ?? (
                <Form.Select
                  id="toolbarMakeCodes"
                  value={availableMakeCodes?.find((code) => code)}
                >
                  {availableMakeCodes?.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Form.Select>
              )}
              <Form.Label>Name</Form.Label>
              <Form.Control
                name="name"
                type="text"
                id="name"
                placeholder={isCreate ? "Name..." : undefined}
                defaultValue={isCreate ? undefined : make.name}
                onChange={handleChange}
                autoFocus={!isCreate}
              />
              {errors.name && (
                <div style={{ marginTop: "1rem" }}>
                  {errors.name.map((e, index) => (
                    <Alert key={e} variant="danger">
                      <p>{e}</p>
                    </Alert>
                  ))}
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button variant="primary" form="makeForm" type="submit">
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default MakeModal;
